import { useCallback, useEffect, useRef, useState } from "react"
import { BroadcastChannel } from "broadcast-channel"

const AUTH_CHANNEL_NAME = "avacus-gas-sponsor-sign"

export type AuthChannelMessage =
  | "signing"
  | "idle"
  | "autoSignStarted"
  | "verified"
  | "syncRequest"

interface UseAuthBroadcastChannelProps {
  isConnected: boolean
  onVerified: () => void
}

/**
 * Single BroadcastChannel for auth: signing state and auto-sign-once across tabs.
 * - "signing" / "idle": one tab is signing so others show disabled/ Signing in other tab
 * - "autoSignStarted": one tab started auto-sign; others must not auto-trigger
 * - "verified": one tab verified; others apply token from cookie and set verified
 * - "syncRequest": a newly opened tab requests existing tabs to re-broadcast their current state (fixes late-joiner issue)
 */
export function useAuthBroadcastChannel({
  isConnected,
  onVerified,
}: UseAuthBroadcastChannelProps) {
  const [isSigningInThisTab, setIsSigningInThisTab] = useState(false)
  const [isSigningInOtherTab, setIsSigningInOtherTab] = useState(false)
  const [hasAutoSignTriggered, setHasAutoSignTriggered] = useState(false)
  const channelRef = useRef<BroadcastChannel<AuthChannelMessage> | null>(null)
  const onVerifiedRef = useRef(onVerified)
  onVerifiedRef.current = onVerified

  const isSigningInThisTabRef = useRef(isSigningInThisTab)
  useEffect(() => {
    isSigningInThisTabRef.current = isSigningInThisTab
  }, [isSigningInThisTab])

  const hasAutoSignTriggeredRef = useRef(hasAutoSignTriggered)
  useEffect(() => {
    hasAutoSignTriggeredRef.current = hasAutoSignTriggered
  }, [hasAutoSignTriggered])

  useEffect(() => {
    const channel = new BroadcastChannel<AuthChannelMessage>(AUTH_CHANNEL_NAME)
    channelRef.current = channel

    channel.onmessage = (msg: AuthChannelMessage) => {
      if (msg === "signing") setIsSigningInOtherTab(true)
      if (msg === "idle") setIsSigningInOtherTab(false)
      if (msg === "autoSignStarted") setHasAutoSignTriggered(true)
      if (msg === "verified") onVerifiedRef.current()
      if (msg === "syncRequest") {
        if (isSigningInThisTabRef.current) channel.postMessage("signing")
        if (hasAutoSignTriggeredRef.current) {
          channel.postMessage("autoSignStarted")
        }
      }
    }

    channel.postMessage("syncRequest")

    return () => {
      channel.close()
      channelRef.current = null
    }
  }, [])

  // Reset hasAutoSignTriggered when user disconnects so next connect can auto-trigger once
  useEffect(() => {
    if (!isConnected) setHasAutoSignTriggered(false)
  }, [isConnected])

  const postSigning = useCallback(() => {
    channelRef.current?.postMessage("signing")
  }, [])

  const postIdle = useCallback(() => {
    channelRef.current?.postMessage("idle")
  }, [])

  const postVerified = useCallback(() => {
    channelRef.current?.postMessage("verified")
  }, [])

  const postAutoSignStarted = useCallback(() => {
    setHasAutoSignTriggered(true)
    channelRef.current?.postMessage("autoSignStarted")
  }, [])

  const wrapVerifyAccount = useCallback(
    (verifyAccountBase: () => Promise<void>) => {
      return async () => {
        const channel = channelRef.current
        if (!channel) return
        postSigning()
        setIsSigningInThisTab(true)
        try {
          await verifyAccountBase()
          postVerified()
        } catch (error) {
          console.error("Verify account failed:", error)
        } finally {
          setIsSigningInThisTab(false)
          postIdle()
        }
      }
    },
    [postSigning, postIdle, postVerified]
  )

  const isSigning = isSigningInThisTab || isSigningInOtherTab

  return {
    wrapVerifyAccount,
    postAutoSignStarted,
    postSigning,
    postIdle,
    isSigningInOtherTab,
    hasAutoSignTriggered,
    isSigning,
  }
}
